// src/pages/offlineSales/customerImportColumns.js
import ExcelJS from 'exceljs';

// Keys must equal the label with spaces/"*" removed (case-insensitive) — the import parser matches on that.
export const TEMPLATE_COLUMNS = [
  { key: 'cusCode',            label: 'Customer Code',        hint: 'Leave blank for new customers — auto-generated' },
  { key: 'name',               label: 'Name *',               hint: 'Customer full name (required for new customers)' },
  { key: 'phone',              label: 'Phone *',              hint: 'Primary phone e.g. +94771234567 — existing phone updates that customer' },
  { key: 'secondaryPhone',     label: 'Secondary Phone',      hint: 'Secondary phone with country code' },
  { key: 'email',              label: 'Email',                hint: 'Customer email address' },
  { key: 'address',            label: 'Address',              hint: 'Street address' },
  { key: 'city',               label: 'City',                 hint: 'Exact city name as registered in the system' },
  { key: 'notes',              label: 'Notes',                hint: 'Internal notes' },
  { key: 'customerType',       label: 'Customer Type',        hint: 'Customer type name as shown in the system' },
  { key: 'creditLimit',        label: 'Credit Limit',         hint: 'Maximum credit amount (blank = no limit)' },
  { key: 'creditTermDays',     label: 'Credit Term Days',     hint: 'Payment due window in days' },
  { key: 'isHardBlock',        label: 'Is Hard Block',        hint: 'Yes / No — block sales over credit limit' },
  { key: 'latitude',           label: 'Latitude',             hint: 'Location latitude' },
  { key: 'longitude',          label: 'Longitude',            hint: 'Location longitude' },
  { key: 'tinNumber',          label: 'TIN Number',           hint: 'Tax identification number' },
  { key: 'vatNumber',          label: 'VAT Number',           hint: 'VAT registration number' },
  { key: 'isActive',           label: 'Is Active',            hint: 'Yes / No' },
  { key: 'openingBalance',     label: 'Opening Balance',      hint: 'Required together with Opening Balance Date (both blank = keep)' },
  { key: 'openingBalanceDate', label: 'Opening Balance Date', hint: 'DD/MM/YYYY — required together with Opening Balance' },
];

const HEADER_COLOR = 'FF667EEA';
const NUMBER_KEYS = ['creditLimit', 'creditTermDays', 'latitude', 'longitude', 'openingBalance'];
export const DATE_KEY = 'openingBalanceDate';
// The date column is Text too: Excel parses typed dates with the OS locale (M/D/Y on US Windows), so
// '11/10/2026' would silently become 10 Nov. As text, what is typed is kept and imported as DD/MM/YYYY.
const TEXT_KEYS = ['cusCode', 'phone', 'secondaryPhone', DATE_KEY];

const toNumber = (value) => (value === null || value === undefined || value === '' ? '' : Number(value));

// 'YYYY-MM-DD' → 'DD/MM/YYYY'
const toSheetDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value || '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '';
};

// Excel data-validation formula: DD/MM/YYYY with a real day (1-31) and month (1-12)
const ddmmyyyyFormula = (ref) => `AND(LEN(${ref})=10,MID(${ref},3,1)="/",MID(${ref},6,1)="/",`
  + `ISNUMBER(DATE(VALUE(RIGHT(${ref},4)),VALUE(MID(${ref},4,2)),VALUE(LEFT(${ref},2)))),`
  + `VALUE(MID(${ref},4,2))>=1,VALUE(MID(${ref},4,2))<=12,VALUE(LEFT(${ref},2))>=1,`
  + `VALUE(LEFT(${ref},2))<=DAY(DATE(VALUE(RIGHT(${ref},4)),VALUE(MID(${ref},4,2))+1,0)))`;

const yesNo = (value) => (value === true ? 'Yes' : value === false ? 'No' : '');

export const toTemplateRow = (customer) => ({
  cusCode: customer.cusCode || '',
  name: customer.name || '',
  phone: customer.phone || '',
  secondaryPhone: customer.secondaryPhone || '',
  email: customer.email || '',
  address: customer.address || '',
  city: customer.city?.name || '',
  notes: customer.notes || '',
  customerType: customer.customerType?.label || '',
  creditLimit: toNumber(customer.creditLimit),
  creditTermDays: toNumber(customer.creditTermDays),
  isHardBlock: yesNo(customer.isHardBlock),
  latitude: toNumber(customer.latitude),
  longitude: toNumber(customer.longitude),
  tinNumber: customer.tinNumber || '',
  vatNumber: customer.vatNumber || '',
  isActive: yesNo(customer.isActive),
  openingBalance: toNumber(customer.openingBalance?.amount),
  openingBalanceDate: toSheetDate(customer.openingBalance?.asOfDate),
});

// Shared by the import template and the customer list export so both files are identical in layout
export const downloadCustomerWorkbook = async (rows, filename) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Rettzy';
  workbook.created = new Date();

  const sheet = workbook.addWorksheet('Customers', { views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }] });
  sheet.columns = TEMPLATE_COLUMNS.map(({ key, label }) => ({ header: label, key, width: key === 'name' ? 24 : 20 }));

  const headerRow = sheet.getRow(1);
  headerRow.eachCell((cell, colNumber) => {
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_COLOR } };
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.alignment = { vertical: 'middle', horizontal: 'left' };
    cell.note = TEMPLATE_COLUMNS[colNumber - 1].hint;
  });
  headerRow.height = 22;

  rows.forEach((row) => sheet.addRow(row));

  NUMBER_KEYS.forEach((key) => { sheet.getColumn(key).numFmt = '0.##'; });
  const textLetters = TEXT_KEYS.map((key) => {
    const column = sheet.getColumn(key);
    column.numFmt = '@';
    return column.letter;
  });
  const dateColumn = sheet.getColumn(DATE_KEY);
  dateColumn.width = 22;

  const lastRow = Math.max(rows.length + 1, 1000);
  const dateLetter = dateColumn.letter;
  for (let r = 2; r <= lastRow; r++) {
    textLetters.forEach((letter) => {
      const textCell = sheet.getCell(`${letter}${r}`);
      textCell.numFmt = '@';
      if (typeof textCell.value === 'number') textCell.value = String(textCell.value);
    });
    sheet.getCell(`${dateLetter}${r}`).dataValidation = {
      type: 'custom',
      allowBlank: true,
      formulae: [ddmmyyyyFormula(`${dateLetter}${r}`)],
      showErrorMessage: true,
      errorTitle: 'Invalid date',
      error: 'Enter the date as DD/MM/YYYY, e.g. 05/10/2026',
      showInputMessage: true,
      promptTitle: 'Opening Balance Date',
      prompt: 'DD/MM/YYYY',
    };
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${filename}.xlsx`;
  anchor.click();
  URL.revokeObjectURL(url);
};
